import { useEffect, useRef } from "react";

/**
 * Hook that animates the favicon to a spinning vinyl when audio is playing.
 * Uses canvas to render frames of a rotating vinyl record.
 */
export function useFavicon(isPlaying: boolean) {
  const frameRef = useRef<number>(0);
  const animationRef = useRef<number | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const linkRef = useRef<HTMLLinkElement | null>(null);
  const originalFaviconRef = useRef<string>("/favicon.svg");

  useEffect(() => {
    // Get or create the favicon link element
    let link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
    if (!link) {
      link = document.createElement("link");
      link.rel = "icon";
      document.head.appendChild(link);
    }
    linkRef.current = link;
    originalFaviconRef.current = link.href;

    // Create canvas for rendering vinyl frames
    if (!canvasRef.current) {
      canvasRef.current = document.createElement("canvas");
      canvasRef.current.width = 32;
      canvasRef.current.height = 32;
    }

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!canvasRef.current || !linkRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const drawVinyl = (rotation: number) => {
      const size = 32;
      const center = size / 2;

      // Clear canvas
      ctx.clearRect(0, 0, size, size);

      // Save context and apply rotation
      ctx.save();
      ctx.translate(center, center);
      ctx.rotate(rotation);
      ctx.translate(-center, -center);

      // Vinyl disc (dark gradient)
      const vinylGradient = ctx.createRadialGradient(
        center,
        center,
        0,
        center,
        center,
        center,
      );
      vinylGradient.addColorStop(0, "#1a1a1a");
      vinylGradient.addColorStop(0.3, "#1a1a1a");
      vinylGradient.addColorStop(0.5, "#222222");
      vinylGradient.addColorStop(0.7, "#1a1a1a");
      vinylGradient.addColorStop(1, "#0a0a0a");

      ctx.beginPath();
      ctx.arc(center, center, center - 1, 0, Math.PI * 2);
      ctx.fillStyle = vinylGradient;
      ctx.fill();

      // Grooves - concentric circles
      ctx.strokeStyle = "rgba(60, 60, 60, 0.4)";
      ctx.lineWidth = 0.5;
      for (let r = 6; r < center - 2; r += 2) {
        ctx.beginPath();
        ctx.arc(center, center, r, 0, Math.PI * 2);
        ctx.stroke();
      }

      // Light reflection
      ctx.beginPath();
      ctx.arc(center, center, center - 2, -0.5, 0.3);
      ctx.strokeStyle = "rgba(255, 255, 255, 0.15)";
      ctx.lineWidth = 2;
      ctx.stroke();

      // Center label
      const labelGradient = ctx.createRadialGradient(
        center,
        center,
        0,
        center,
        center,
        8,
      );
      labelGradient.addColorStop(0, "#f0f0f0");
      labelGradient.addColorStop(1, "#d0d0d0");

      ctx.beginPath();
      ctx.arc(center, center, 7, 0, Math.PI * 2);
      ctx.fillStyle = labelGradient;
      ctx.fill();

      // "SE" text
      ctx.restore(); // Restore before drawing text so it doesn't rotate with vinyl

      ctx.font = "bold 6px system-ui, sans-serif";
      ctx.fillStyle = "#1a1a1a";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText("SE", center, center + 0.5);

      // Spindle hole
      ctx.beginPath();
      ctx.arc(center, center, 1.5, 0, Math.PI * 2);
      ctx.fillStyle = "#0a0a0a";
      ctx.fill();
    };

    const animate = () => {
      if (!isPlaying) return;

      frameRef.current += 0.15; // Speed of rotation
      drawVinyl(frameRef.current);

      // Update favicon
      if (linkRef.current) {
        linkRef.current.href = canvas.toDataURL("image/png");
      }

      animationRef.current = requestAnimationFrame(animate);
    };

    if (isPlaying) {
      animate();
    } else {
      // Reset to static vinyl when stopped
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
        animationRef.current = null;
      }

      // Draw static vinyl
      drawVinyl(0);
      if (linkRef.current) {
        linkRef.current.href = canvas.toDataURL("image/png");
      }
    }

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [isPlaying]);
}
